/*
  Partial v4 (2026-10-29) — motion and small interactions for the "In Motion" design.
  Motion IDs match the Motion spec frame in Figma (page "V2 — In Motion").

  Rules: everything settles in under a second, nothing loops, only transform/opacity/clip-path
  animate, and visitors with "reduce motion" get the still page. If GSAP fails to load, every
  element is shown as-is and the interactions (carousel arrows, card +, filters) still work.
*/
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function reveal() { root.classList.add('p-motion-ready'); }
  // Safety net: never leave hidden text on screen if something goes wrong.
  setTimeout(reveal, 2500);

  /* ---------------- Interactions (no GSAP needed) ---------------- */

  // M4: carousel arrows scroll one card at a time.
  function initCarousels() {
    document.querySelectorAll('[data-p-carousel]').forEach(function (wrap) {
      var track = wrap.querySelector('[data-p-track]');
      var prev = wrap.querySelector('[data-p-prev]');
      var next = wrap.querySelector('[data-p-next]');
      if (!track) return;
      function step() {
        var card = track.querySelector('[data-p-card]');
        return card ? card.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      }
      function updateArrows() {
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      }
      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }); });
      track.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', updateArrows);
      updateArrows();
    });
  }

  // M5: + opens the card's story.
  function initCardExpand() {
    document.querySelectorAll('[data-p-expand]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('[data-p-card]');
        var story = document.getElementById(btn.getAttribute('aria-controls'));
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        card.classList.toggle('is-open', open);
        if (story) {
          story.hidden = !open;
          if (open && window.gsap && !reduce) {
            window.gsap.fromTo(story, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.4, delay: 0.15, ease: 'power2.out' });
          }
        }
        if (open) card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
      });
    });
  }

  // M9: shop filter chips. With GSAP Flip the grid re-flows smoothly; without it, it just updates.
  function initFilters() {
    document.querySelectorAll('[data-p-filter-root]').forEach(function (rootEl) {
      var chips = rootEl.querySelectorAll('[data-p-filter]');
      var grid = rootEl.querySelector('[data-p-filter-grid]');
      if (!chips.length || !grid) return;
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var type = chip.dataset.pFilter;
          var items = grid.querySelectorAll('[data-p-type], [data-p-editorial]');
          var Flip = window.Flip;
          var state = Flip && !reduce ? Flip.getState(items) : null;
          chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
          items.forEach(function (item) {
            if (item.hasAttribute('data-p-editorial')) { item.hidden = type !== 'all'; return; }
            item.hidden = !(type === 'all' || item.dataset.pType === type);
          });
          if (state) {
            Flip.from(state, {
              duration: 0.5, ease: 'power2.inOut', absolute: true,
              onEnter: function (els) { return window.gsap.fromTo(els, { opacity: 0, scale: 0.96 }, { opacity: 1, scale: 1, duration: 0.4 }); },
              onLeave: function (els) { return window.gsap.to(els, { opacity: 0, scale: 0.96, duration: 0.3 }); }
            });
          }
        });
      });
    });
  }

  /* ---------------- Scroll and load motion (GSAP) ---------------- */

  function initMotion() {
    var gsap = window.gsap;
    if (!gsap || reduce) { reveal(); return; }
    var plugins = [window.ScrollTrigger, window.SplitText, window.Flip].filter(Boolean);
    gsap.registerPlugin.apply(gsap, plugins);
    var ST = window.ScrollTrigger;
    var Split = window.SplitText;

    var mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', function () {
      // M1: headline words rise in on load.
      document.querySelectorAll('[data-p-headline]').forEach(function (el) {
        if (!Split) return;
        Split.create(el, {
          type: 'words', mask: 'words', autoSplit: true,
          onSplit: function (self) {
            return gsap.from(self.words, { yPercent: 100, opacity: 0, duration: 0.8, ease: 'power3.out', stagger: 0.06, delay: 0.1 });
          }
        });
      });

      // M13: lines rise out of a mask on load.
      document.querySelectorAll('[data-p-lines]').forEach(function (el) {
        if (!Split) return;
        Split.create(el, {
          type: 'lines', mask: 'lines', autoSplit: true,
          onSplit: function (self) {
            return gsap.from(self.lines, { yPercent: 100, duration: 0.9, ease: 'power3.out', stagger: 0.1, delay: 0.1 });
          }
        });
      });
      reveal();

      if (!ST) return;

      // M2: columns drift at slightly different speeds (desktop only).
      mm.add('(min-width: 750px)', function () {
        document.querySelectorAll('[data-p-speed]').forEach(function (el) {
          var speed = parseFloat(el.dataset.pSpeed) || 1;
          if (speed === 1) return;
          var dist = (1 - speed) * 600; // 0.9 → +60px, 1.1 → -60px across the section
          gsap.fromTo(el, { y: -dist / 2 }, {
            y: dist / 2, ease: 'none',
            scrollTrigger: { trigger: el.closest('[data-p-parallax]') || el, start: 'top bottom', end: 'bottom top', scrub: true }
          });
        });
      });

      // M3: statement words fill from faint to full ink as they pass the middle of the screen.
      document.querySelectorAll('[data-p-reveal]').forEach(function (el) {
        if (!Split) return;
        Split.create(el, {
          type: 'words', autoSplit: true,
          onSplit: function (self) {
            return gsap.fromTo(self.words, { opacity: 0.2 }, {
              opacity: 1, stagger: 0.1, ease: 'none',
              scrollTrigger: { trigger: el, start: 'top 80%', end: 'bottom 45%', scrub: true }
            });
          }
        });
      });

      // M7: photo wipes up into view while settling from 108% to 100%.
      document.querySelectorAll('[data-p-wipe]').forEach(function (el) {
        var img = el.querySelector('img');
        var tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 80%', once: true } });
        tl.fromTo(el, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1, ease: 'power3.out' });
        if (img) tl.fromTo(img, { scale: 1.08 }, { scale: 1, duration: 1.2, ease: 'power3.out' }, 0);
      });

      // M8: cards rise in, one after another.
      document.querySelectorAll('[data-p-rise]').forEach(function (group) {
        var kids = Array.prototype.slice.call(group.children);
        if (!kids.length) return;
        gsap.from(kids, {
          y: 40, opacity: 0, duration: 0.6, ease: 'power2.out', stagger: 0.1,
          scrollTrigger: { trigger: group, start: 'top 85%', once: true }
        });
      });
    });
  }

  function boot() {
    initCarousels();
    initCardExpand();
    initFilters();
    initMotion();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
